import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from '../../config/axiosConfig';

// Import tab components
import PortfolioInsightsPerformance from './PortfolioInsightsPerformance';
import PortfolioInsightsFinancials from './PortfolioInsightsFinancials';
import PortfolioInsightsAnalysts from './PortfolioInsightsAnalysts';
import PortfolioInsightsDividends from './PortfolioInsightsDividends';
import PortfolioInsightsRisk from './PortfolioInsightsRisk';

// Tab Panel component
const TabPanel = ({ children, value, index, ...other }) => {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`portfolio-insights-tabpanel-${index}`}
            aria-labelledby={`portfolio-insights-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

// Main PortfolioInsights Component
const PortfolioInsights = ({ holdings = [], baseCurrency = 'USD' }) => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);

    // Centralized data state
    const [performanceData, setPerformanceData] = useState([]);
    const [spyData, setSpyData] = useState(null);
    const [financialData, setFinancialData] = useState([]);
    const [analystData, setAnalystData] = useState([]);
    const [dividendData, setDividendData] = useState([]);
    const [riskData, setRiskData] = useState(null);

    // Get unique symbols
    const symbols = [...new Set(holdings.map(h => h.symbol))];
    const symbolsKey = symbols.join(',');

    useEffect(() => {
        const fetchAllData = async () => {
            if (symbols.length === 0) {
                setLoading(false);
                return;
            }

            setLoading(true);

            try {
                // Fetch main data in parallel
                const [perfResponse, spyResponse, finResponse, analystResponse, divResponse] = await Promise.all([
                    axios.post('/api/stock-price-change/batch', { symbols }),
                    axios.get('/api/stock-price-change/SPY'),
                    axios.get('/api/portfolio/financials-summary', { params: { symbols: symbolsKey } }),
                    axios.get('/api/portfolio/analysts-summary', { params: { symbols: symbolsKey } }),
                    axios.get('/api/portfolio/dividends-summary', { params: { symbols: symbolsKey } })
                ]);

                // Set performance data
                if (perfResponse.data.success) {
                    setPerformanceData(perfResponse.data.data);
                }

                // Set SPY benchmark data
                if (spyResponse.data.success) {
                    setSpyData(spyResponse.data.data);
                }

                // Set financials data
                if (finResponse.data.data) {
                    setFinancialData(finResponse.data.data);
                }

                // Set analyst data
                if (analystResponse.data.data) {
                    setAnalystData(analystResponse.data.data);
                }

                // Set dividend data
                if (divResponse.data.data) {
                    setDividendData(divResponse.data.data);
                }

                // Fetch risk data separately (may fail if insufficient price history)
                try {
                    const riskResponse = await axios.post('/api/portfolio/risk-metrics', { holdings });
                    if (riskResponse.data.success) {
                        setRiskData(riskResponse.data.data);
                    }
                } catch (riskError) {
                    console.error('Risk metrics unavailable:', riskError);
                }

            } catch (error) {
                console.error('Error fetching portfolio insights data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbolsKey]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Don't render if no holdings
    if (!holdings || holdings.length === 0) {
        return null;
    }

    const tabs = ['Performance', 'Financials', 'Analysts', 'Dividends', 'Risk'];

    return (
        <Box sx={{ mt: 3 }}>
            <Box sx={{
                bgcolor: theme.palette.background.paper,
                borderRadius: 1,
                boxShadow: 10,
                overflow: 'hidden'
            }}>
                {/* Tabs Header */}
                <Box sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    px: 2,
                    pt: 2
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        TabIndicatorProps={{
                            style: { display: 'none' }
                        }}
                        sx={{
                            minHeight: 40,
                            '& .MuiTabs-flexContainer': {
                                gap: 1,
                            },
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.875rem',
                                minHeight: 36,
                                minWidth: 'auto',
                                px: 2.5,
                                py: 1,
                                borderRadius: '20px',
                                color: theme.palette.text.secondary,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: theme.palette.action.hover,
                                },
                                '&.Mui-selected': {
                                    color: theme.palette.common.white,
                                    bgcolor: theme.palette.primary.main,
                                },
                            },
                        }}
                    >
                        {tabs.map((label) => (
                            <Tab key={label} label={label} />
                        ))}
                    </Tabs>
                </Box>

                {/* Loading State */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Tab Panels */}
                        <TabPanel value={activeTab} index={0}>
                            <PortfolioInsightsPerformance
                                holdings={holdings}
                                baseCurrency={baseCurrency}
                                performanceData={performanceData}
                                spyData={spyData}
                            />
                        </TabPanel>
                        <TabPanel value={activeTab} index={1}>
                            <PortfolioInsightsFinancials
                                holdings={holdings}
                                baseCurrency={baseCurrency}
                                financialData={financialData}
                            />
                        </TabPanel>
                        <TabPanel value={activeTab} index={2}>
                            <PortfolioInsightsAnalysts
                                holdings={holdings}
                                analystData={analystData}
                            />
                        </TabPanel>
                        <TabPanel value={activeTab} index={3}>
                            <PortfolioInsightsDividends
                                holdings={holdings}
                                baseCurrency={baseCurrency}
                                dividendData={dividendData}
                            />
                        </TabPanel>
                        <TabPanel value={activeTab} index={4}>
                            <PortfolioInsightsRisk
                                holdings={holdings}
                                riskData={riskData}
                            />
                        </TabPanel>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default PortfolioInsights;